import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import emailMask from 'text-mask-addons/dist/emailMask';
import { get, isNil, isEmpty } from 'lodash';
import moment from 'moment';
import { ChevronDown } from 'lucide-react';

import { getLoanPurposesAction } from 'actions/lookup';
import { getIPAddress, nextAction } from 'actions/workflow';
import { Select, FormColumn, FormRow, Input, Checkbox, AddressInput } from 'components/Form';
import Validator from 'components/Validator';
import { BackButton, Button } from 'components/Button';
import GeneralError from 'components/GeneralError';
import Alert from 'components/Alert';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { dateMask, numberUnmask, phoneMask, ssnMask } from 'utils/masks';
import { toTitleCase } from 'utils/format';
import { mapLookupToOptions } from 'utils/objects';
import { useConfig } from 'config/ConfigContext';
import { states } from 'utils/states';
import { popoverLinks, PRESET_OPTIONS } from 'utils/constants';

import schema from './schema';

const PersonalInfo = ({
  navigate,
  searchParams,
  dispatch,
  validator: { values, errors, onChangeHandler, setValues, validate },
}) => {
  useWorkflowGuard(searchParams, navigate);
  const { envLabel } = useConfig();
  const checkinData = useSelector((state) => get(state, 'workflow.data'));

  const [purposeOptions, setPurposeOptions] = useState([]);
  const [generalError, setGeneralError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isLoadingPurpose, setIsLoadingPurpose] = useState(false);
  const [maskedInputKey, setMaskedInputKey] = useState(0);
  const [ipAddress, setIpAddress] = useState(null);
  const [isPresetMenuOpen, setIsPresetMenuOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [skipIdScan, setSkipIdScan] = useState(false);
  const [skipDuplicationCheck, setSkipDuplicationCheck] = useState(false);
  const [hasOpenedPresetMenu, setHasOpenedPresetMenu] = useState(false);

  const presetMenuRef = useRef(null);

  const fetchLoanPurposes = () => {
    setIsLoadingPurpose(true);
    dispatch(getLoanPurposesAction({
      success: (res) => {
        setPurposeOptions(mapLookupToOptions(res));
        setIsLoadingPurpose(false);
      },
      fail: () => {
        setIsLoadingPurpose(false);
      }
    }));
  };
  
  const fetchIpAddress = () => {
    dispatch(getIPAddress({
      success: (ipResponse) => {
        setIpAddress(ipResponse.ip);
      },
      fail: (err) => {
        setIpAddress('static ip');
        console.log(err, 'Getting IP address failed');
      },
    }));
  }

  useEffect(() => {
    fetchLoanPurposes();
    fetchIpAddress();
  }, []);

  useEffect(() => {
    if (!isEmpty(checkinData) && !isNil(checkinData)) {
      setMaskedInputKey((k) => k + 1);
      setValues({
        city: get(checkinData, 'city'),
        dateOfBirth: get(checkinData, 'dateOfBirth')
          ? moment(get(checkinData, 'dateOfBirth')).format('MM/DD/YYYY')
          : undefined,
        email: get(checkinData, 'email'),
        firstName: get(checkinData, 'firstName'),
        lastName: get(checkinData, 'lastName'),
        mobileNumber: get(checkinData, 'mobileNumber'),
        ssn: get(checkinData, 'ssn'),
        state: get(checkinData, 'state')
          ? states.find((s) => s.value === get(checkinData, 'state'))
          : undefined,
        streetAddress: get(checkinData, 'streetAddress'),
        zip: get(checkinData, 'zipCode'),
        purpose: get(checkinData, 'purpose')
          ? (purposeOptions || []).find((p) => p.value === get(checkinData, 'purpose'))
          : undefined,
      });
    }
  }, [checkinData, purposeOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (presetMenuRef.current && !presetMenuRef.current.contains(event.target)) {
        setIsPresetMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (field, value) => {
    onChangeHandler(field, value);
  };

  const handleBlur = (field) => () => {
    const value = get(values, field);
    if (!value) return;

    switch (field) {
      case 'firstName':
      case 'lastName':
        onChangeHandler(field, toTitleCase(value));
        break;
      case 'email':
        onChangeHandler(field, value.toLowerCase());
        break;
      default:
        break;
    }
  };

  const onAddressSave = (addressValues) => {
    setValues({
      ...values,
      ...addressValues,
    });
  };

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSubmitting(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setGeneralError(null);

    if (validate().isValid) {
      setIsSubmitting(true);
      dispatch(nextAction({
        key: searchParams.get('key'),
        data: {
          firstName: get(values, 'firstName').trim(),
          lastName: get(values, 'lastName').trim(),
          streetAddress: get(values, 'streetAddress'),
          city: get(values, 'city'),
          state: get(values, 'state.value'),
          zipCode: get(values, 'zip'),
          dateOfBirth: get(values, 'dateOfBirth'),
          ssn: numberUnmask(get(values, 'ssn')),
          email: get(values, 'email'),
          mobileNumber: numberUnmask(get(values, 'mobileNumber')),
          purpose: get(values, 'purpose.value'),
          skipIdScan,
          skipDuplicationCheck,
          irclickid: searchParams.get('irclickid') || undefined,
          partnername: searchParams.get('partnername') || undefined,
          sourceIp: ipAddress,
        },
        success: (res) => {
          navigate(get(res, 'data.url'));
          setIsSubmitting(false);
        },
        fail: handleFail,
      }));
    }
  };

  const applyPresetValues = (preset) => {
    const presetData = JSON.parse(preset);
    setMaskedInputKey((k) => k + 1);
    setValues({
      ...values,
      ...presetData,
      purpose: purposeOptions[0],
    });

    setConsentChecked(true);
  };

  const handlePresetSelect = (option) => {
    setSelectedPreset(option);
    applyPresetValues(JSON.stringify(option.value));
    setIsPresetMenuOpen(false);
  };

  const addressInitialValues = useMemo(() => ({
    streetAddress: values.streetAddress,
    zip: values.zip,
    state: values.state,
    city: values.city,
  }), [values.streetAddress, values.zip, values.state, values.city]);

  return (
    <div className="relative">
      {envLabel && envLabel !== 'production' ? (
        <div ref={presetMenuRef} className="absolute -top-[5rem] left-0 z-20">
          <button
            type="button"
            onClick={() => {
              setIsPresetMenuOpen((prev) => !prev);
              if (!hasOpenedPresetMenu) {
                setSkipDuplicationCheck(true);
                setHasOpenedPresetMenu(true);
              }
            }}
            className="
              inline-flex items-center gap-2
              rounded-md bg-blue-500 px-3 py-2
              text-xs font-medium text-white
              transition hover:bg-blue-600
              focus:outline-none focus:ring-2
              focus:ring-offset-2 focus:ring-blue-500
            "
          >
            <span>Set Values</span>
            <ChevronDown
              size={14}
              className={`transition-transform duration-200 ${isPresetMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isPresetMenuOpen && (
            <div className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
              <div className="py-1">
                {PRESET_OPTIONS.map((option) => {
                  const isActive = selectedPreset?.value === option.value;

                  return (
                    <button
                      key={JSON.stringify(option.value)}
                      type="button"
                      onClick={() => handlePresetSelect(option)}
                      className={`
                        w-full px-3 py-2 text-left text-sm transition-colors duration-150
                        ${isActive ? 'bg-blue-500 text-white' : 'text-slate-700 hover:bg-gray-100'}
                      `}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <div className="border-t border-slate-200 bg-slate-50 px-3 py-3 space-y-3">
                <Checkbox
                  label="Skip Id Scan"
                  checked={skipIdScan}
                  onChange={setSkipIdScan}
                  className="text-xs"
                />

                <Checkbox
                  label="Ignore Dupe Rules"
                  checked={skipDuplicationCheck}
                  onChange={setSkipDuplicationCheck}
                  className="text-xs"
                />
              </div>
            </div>
          )}
        </div>
      ) : null}

      <FormRow>
        <FormColumn>
          <Input
            label="First Name"
            placeholder="First Name"
            value={values.firstName}
            error={errors.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            onBlur={handleBlur('firstName')}
            required
          />
        </FormColumn>
        <FormColumn>
          <Input
            label="Last Name"
            placeholder="Last Name"
            value={values.lastName}
            error={errors.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            onBlur={handleBlur('lastName')}
            required
          />
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <AddressInput
            label="Address"
            required
            initialValues={addressInitialValues}
            onSave={onAddressSave}
            error={errors.address}
          />
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <Input
            label="Date of Birth"
            placeholder="MM/DD/YYYY"
            inputClassName="font-numeric"
            mask={dateMask}
            key={`dob-${maskedInputKey}`}
            value={values.dateOfBirth}
            error={errors.dateOfBirth}
            onChange={(e) => handleChange('dateOfBirth', e.target.value)}
            required
            inputMode="numeric"
          />
        </FormColumn>
        <FormColumn>
          <Input
            inputClassName="font-numeric"
            key={`ssn-${maskedInputKey}`}
            value={values.ssn}
            error={errors.ssn}
            onChange={(e) => handleChange('ssn', e.target.value)}
            label="Social Security Number (SSN)"
            mask={ssnMask}
            placeholder="___-__-____"
            required
            inputMode="numeric"
          />
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <Input
            label="Email Address"
            placeholder="e.g. email@company.com"
            mask={emailMask}
            key={`email-${maskedInputKey}`}
            value={values.email}
            error={errors.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={handleBlur('email')}
            required
          />
        </FormColumn>
        <FormColumn>
          <Input
            inputClassName="font-numeric"
            label="Mobile Number"
            placeholder="(+1)"
            mask={phoneMask}
            key={`phone-${maskedInputKey}`}
            value={values.mobileNumber}
            error={errors.mobileNumber}
            onChange={(e) => handleChange('mobileNumber', e.target.value)}
            required
            inputMode="numeric"
          />
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <Select
            label="Loan Purpose"
            required
            options={purposeOptions}
            value={values.purpose}
            onChange={(val) => handleChange('purpose', val)}
            placeholder="Select a purpose"
            error={errors.purpose}
            isLoading={isLoadingPurpose}
          />
        </FormColumn>
      </FormRow>

      <div className="border-t border-slate-300 pt-6 mt-6">
        <Checkbox
          className="mb-6 text-xs"
          label="By checking this box, I consent to receive calls and texts for any purpose, including marketing, under these terms. I also agree to the Electronic Consent, Terms and Conditions, and Acknowledgements and Disclosures."
          checked={consentChecked}
          onChange={setConsentChecked}
          popoverLinks={popoverLinks}
          required
        />
      </div>

      <Alert
        className="mb-6"
        title="Important information about procedures for opening a new account"
        expandable
      >
        <p>
          To help the government fight the funding of terrorism and money laundering activities, Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account.
        </p>

        <p className="mt-3">
          <span className="font-semibold">What this means for you:</span>
          {' '}
          When you open an account, we will ask for your name, address, date of birth, and other information that will allow us to identify you. We may also ask to see your driver's license or other identifying documents.
        </p>
      </Alert>

      <GeneralError error={generalError} />

      <FormRow className="flex-col-reverse">
        <BackButton
          className="flex-1"
          onFail={handleFail}
          onBeforeBack={() => setGeneralError(null)}
        />
        <Button
          className="flex-1"
          disabled={!consentChecked}
          label="Next Step: Verify Identity"
          onClick={handleSubmit}
          isLoading={isSubmitting}
        />
      </FormRow>
    </div>
  );
};

PersonalInfo.propTypes = {
  validator: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default Validator(schema)(PersonalInfo);
